import React, { useCallback, useEffect, useMemo, useReducer } from 'react';
import axios from 'axios';
import Helpers, { validateEmail } from '../../helpers';
import Config from '../../config';
import {
    Switch,
    Chip,
    Grid,
    Card,
    Toolbar,
    CardContent,
    TextField,
    Button,
    Dialog,
    DialogContent,
    Typography,
    DialogActions,
    Select,
    MenuItem,
    Accordion as MuiAccordion,
    AccordionDetails as MuiAccordionDetails,
    IconButton,
    Link as MuiLink,
    Tooltip,
    Autocomplete,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Timeline, TimelineEvent } from 'react-event-timeline';
import LogIcon from '@mui/icons-material/Check';
import Moment from 'react-moment';
import ExtendedField from '../../components/text_field';
import ConfirmDialog from '../../components/confirm_dialog';
import { Form, Field } from 'react-final-form';
import DownloadButton from '../../components/download_button';
import moment from 'moment';
import ExtendedSelectBase from '../../components/select';
import { PriorityChip } from './onboarding';
import { RetailerOnboarding, loadPartnerDetails, PartnerOnboardingTasks } from './supplier_onboarding';
import { fetchAllODataPages } from './utils/odata_utils';
import { utilGetPartners } from '../../utils/utils';
import isEqual from 'lodash.isequal';
import debounce from 'lodash.debounce';
import ContentLoader from '../../components/content_loader';
import { ExpandMore, ExpandLess, ArrowBack, InfoOutlined, ErrorOutline as ErrorIcon } from '@mui/icons-material';
import { useHistory, useLocation, Link as RouterLink } from 'react-router-dom';

import SectionToolbar from './section_toolbar';
import { pageContentGap } from '../../theme';
import {
    IntegrationDetailModel,
    IntegrationLog,
    IntegrationRecord,
    OnboardingCustomFieldConfig,
    TestCaseGroupsConfig,
} from './onboarding_details.types';

// ExtendedSelect is a JS component wrapped in forwardRef; its inferred type
// only exposes RefAttributes, so cast to ComponentType<any> to allow the
// variant/options/onChange/style props (same pattern as supplier_onboarding.tsx).
const ExtendedSelect = ExtendedSelectBase as React.ComponentType<any>;

const iconButtonSx = (theme: any) => ({
    backgroundColor: "white",
    border: "none",
    borderRadius: "6px",
    "& svg": { color: "rgba(0, 0, 0, 0.54)" }
});

interface LogProps {
    log: IntegrationLog;
    contentStyle?: React.CSSProperties;
}

const Log = ({ log, contentStyle }: LogProps) => {
    return (
        <TimelineEvent
            title={<>{log.Message}</>}
            contentStyle={contentStyle || { boxShadow: 'none', padding: 0 }}
            createdAt={<><b>{log.Username}</b> on <Moment format='MM/DD/YYYY hh:mm A'>{log.Date}</Moment></>}
            icon={<LogIcon style={{ fontSize: '20px' }} />}
            iconColor={COLORS.UI.ICON_DEFAULT}
        />
    );
};

interface WrappedFieldProps {
    label: React.ReactNode;
    children?: React.ReactNode;
    innerStyle?: React.CSSProperties;
    labelStyle?: React.CSSProperties;
}

const WrappedField = ({ label, children, innerStyle, labelStyle }: WrappedFieldProps) => {
    return (
        <div style={{ display: "flex", minHeight: "2.5em" }}>
            <div style={{ width: "20em", display: "inline-block", fontWeight: "bold", paddingTop: "4px", ...labelStyle }}>{label}</div>
            <div style={{ display: "inline-flex", flex: 1, ...innerStyle }}>{children}</div>
        </div>
    );
};
const Accordion = styled(MuiAccordion)({
    "&:before": {
        display: 'none'
    }
});
const AccordionDetails = styled(MuiAccordionDetails)({
    paddingRight: 0,
    paddingLeft: 0
});
// Color constants for status and UI elements
// TODO: Move these to a general location for reuse
const COLORS = {
    STATUS: {
        NEW: '#275E7A',
        IN_PROGRESS: '#ffa500',
        LIVE: '#448044',
        ERROR: '#EE3224'
    },
    TEXT: {
        PRIMARY: '#111827',
        SECONDARY: '#6B7280',
        WHITE: '#fff'
    },
    UI: {
        BORDER: '#E5E7EB',
        ICON_DEFAULT: '#737373',
        LOADER_PRIMARY: '#e1e1e1',
        LOADER_SECONDARY: '#d8d8d8'
    }
};

interface PartnerOption {
    label: string;
    value: string;
}

interface OnboardingDetailsV2State {
    showConfirm: boolean;
    showError: boolean;
    showReject: boolean;
    showRejectGoLive: boolean;
    showResendFirstEmail: boolean;
    showLinkToExisting: boolean;
    tags: PartnerOption[];
    customFields: OnboardingCustomFieldConfig[];
    shippingLabelEnabled: boolean;
    testCaseGroups: TestCaseGroupsConfig | Record<string, never>;
    integration: IntegrationDetailModel | null;
    tasks: PartnerOnboardingTasks | null;
    rejectMessage: string;
    confirmMessage: string;
    onConfirm: (() => void) | null;
    error: string;
    showOnboardingForm: boolean;
    isSubmitting: boolean;
    existingPartners: PartnerOption[];
    isEditingCustomFields: boolean;
    customFieldValues: Record<string, string | null>;
}

const initialState: OnboardingDetailsV2State = {
    showConfirm: false,
    showError: false,
    showReject: false,
    showRejectGoLive: false,
    showResendFirstEmail: false,
    showLinkToExisting: false,
    tags: [],
    customFields: [],
    shippingLabelEnabled: false,
    testCaseGroups: {},
    integration: null,
    tasks: null,
    rejectMessage: '',
    confirmMessage: '',
    onConfirm: null,
    error: '',
    showOnboardingForm: true,
    isSubmitting: false,
    existingPartners: [],
    isEditingCustomFields: false,
    customFieldValues: {}
};
const reducer = (state: OnboardingDetailsV2State, updates: Partial<OnboardingDetailsV2State>): OnboardingDetailsV2State => ({ ...state, ...updates });

// Multi-select answers are stored comma-joined (ODO settings reject commas in
// options), matching the supplier-facing onboarding form.
const splitMultiValue = (value: string | null | undefined): string[] => (value || '').split(',').map(r => r.trim()).filter(Boolean);
const normalizeCustomFieldValue = (value: unknown): string => (value === null || value === undefined ? '' : String(value));

const getPartnerCoId =(partner: any): string | number | null => partner?.CoId ?? partner?.Id ?? null;

// fetchAllODataPages moved to ./utils/odata_utils for reuse across the modal
// and the detail page; the import lives at the top of this file.

interface OnboardingDetailsV2Props {
    setTitle?: (title: string, includeInRecents?: boolean) => void;
    isRetailer?: boolean;
    history?: any;
    location?: any;
    match?: any;
}

const OnboardingDetailsV2 = ({ setTitle }: OnboardingDetailsV2Props) => {
    const [state, setState] = useReducer(reducer, initialState);
    const history = useHistory();
    const location = useLocation();
    const loadIntegration = useCallback(async () => {
        try {
            let integrationId = new Helpers().queryString()["id"];
            let integration: IntegrationDetailModel = (await axios.get(Config.oauth + '/integration/getintegration?id=' + integrationId)).data;
            if (integration.Logs) {
                integration.Logs = integration.Logs.sort((a, b) => (moment(b.Date).valueOf() - moment(a.Date).valueOf()));
            }
            integration.StatusColor = COLORS.STATUS.IN_PROGRESS;
            if (integration.IsLive) {
                integration.StatusColor = COLORS.STATUS.LIVE;
            } else if (integration.Integration.Status === "New") {
                integration.StatusColor = COLORS.STATUS.NEW;
            }
            if (integration.Integration.RequestedLiveDate) {
                integration.Integration.RequestedLiveDate = moment(integration.Integration.RequestedLiveDate).utc().format('YYYY-MM-DD');
            }
            if (integration.Integration.Tags) {
                integration.Integration.Tags = (integration.Integration.Tags as string).split("|").filter(Boolean);
            }
            integration.AccountType = "New";
            if (integration.Integration.ExistingAccount) {
                integration.AccountType = "Existing";
            }
            if (setTitle && integration.Integration && integration.Integration.CompanyName) {
                setTitle(`Onb: ${integration.Integration.CompanyName}`, true);
            }
            let tasksData = await loadPartnerDetails(integration.Integration);
            setState({ integration, tasks: tasksData });
        } catch (e) {
            console.error('Failed to load integration:', e);
            setState({ integration: null, tasks: null });
        }
    }, [setTitle]);
    useEffect(() => {
        let isCancelled = false;
        const initialize = async () => {
            let config = (await axios.get(Config.oauth + '/integration/getconfig')).data;
            let tags = config.Tags || [];
            let testCaseGroups: TestCaseGroupsConfig | Record<string, never> = {};
            if (!Config.production) {
                testCaseGroups = (await axios.get(Config.api + '/odata/Company/Functions.GetTestCaseGroups')).data;
            }
            if (isCancelled) {
                return;
            }
            setState({ tags: tags.map((r: string) => ({ label: r, value: r })), customFields: config.CustomFields || [], shippingLabelEnabled: !(!config.EnableShippingLabelStep), testCaseGroups });
            if (!isCancelled) {
                loadIntegration();
            }
        };
        initialize();
        return () => { isCancelled = true; };
    }, [loadIntegration]);
    const { integration, tasks, tags, customFields, shippingLabelEnabled, testCaseGroups, showReject, showRejectGoLive, showResendFirstEmail, showLinkToExisting, existingPartners, showError, showConfirm, confirmMessage, onConfirm, error, showOnboardingForm, isSubmitting, isEditingCustomFields, customFieldValues } = state;
    const handleBack = useCallback((event: React.MouseEvent) => {
        event.preventDefault();
        if (!history) {
            return;
        }
        if (!location || location.key === undefined) {
            history.push('/onboarding');
        } else {
            history.goBack();
        }
    }, [history, location]);
    const updateIntegration = useCallback(async (url: string, data: any, optimisticUpdate: Partial<IntegrationRecord> | null = null) => {
        if (!integration) {
            return;
        }
        // Store previous state for potential reversion
        const previousIntegration = integration;
        // Apply optimistic update immediately if provided
        if (optimisticUpdate) {
            setState({
                integration: {
                    ...integration,
                    Integration: {
                        ...integration.Integration,
                        ...optimisticUpdate
                    }
                }
            });
        }
        try {
            await axios.post(Config.oauth + url + integration.Integration.Id, data);
            // Success - no need to reload, state is already updated
        } catch (err: any) {
            console.error('Failed to update integration:', err);
            // Revert optimistic update on failure
            if (optimisticUpdate) {
                setState({ integration: previousIntegration });
            }
            let errorMsg = 'Unexpected error.';
            if (err.response && err.response.data && err.response.data.error) {
                errorMsg = err.response.data.error;
            }
            setState({ showError: true, error: errorMsg });
        }
    }, [integration]);
    const setPriority = async (e: any) => {
        if (!integration) {
            return;
        }
        let data = { Priority: e.target.value };
        if (data.Priority === integration.Integration.Priority) {
            return;
        }
        await updateIntegration('/integration/OperatorUpdatePriority/', data, { Priority: data.Priority });
    };
    const setTags = async (e: any) => {
        if (!integration) {
            return;
        }
        let data = { Tags: e };
        if (isEqual(data.Tags, integration.Integration.Tags)) {
            return;
        }
        await updateIntegration('/integration/OperatorUpdateTags/', data, { Tags: data.Tags });
    };
    const setReminders = async (e: any) => {
        if (!integration) {
            return;
        }
        let data = { DisableReminders: e.target.checked };
        if (data.DisableReminders === integration.Integration.DisableReminders) {
            return;
        }
        await updateIntegration('/integration/OperatorUpdateReminder/', data, { DisableReminders: data.DisableReminders });
    };
    const setIntegrationType = async (e: any) => {
        if (!integration) {
            return;
        }
        let data = { IntegrationType: e.target.value };
        if (data.IntegrationType === integration.Integration.IntegrationType) {
            return;
        }
        await updateIntegration('/integration/OperatorUpdateType/', data, { IntegrationType: data.IntegrationType });
    };
    const setGoLiveDate = async (e: any) => {
        if (!integration) {
            return;
        }
        let data = { RequestedLiveDate: moment(e.target.value).format('YYYY-MM-DD') };
        if (data.RequestedLiveDate === integration.Integration.RequestedLiveDate) {
            return;
        }
        await updateIntegration('/integration/OperatorUpdateRequestedLiveDate/', data, { RequestedLiveDate: data.RequestedLiveDate });
    };
    const setTestGroup = async (e: any) => {
        if (!integration) {
            return;
        }
        let data = { TestGroup: e.target.value };
        if (data.TestGroup === integration.Integration.TestGroup) {
            return;
        }
        await updateIntegration('/integration/OperatorUpdateTestGroup/', data, { TestGroup: data.TestGroup });
        // Make the secondary API call to update system settings
        if (integration.Integration.CoId) {
            try {
                let url = Config.api + `/odata/Company/Functions.SetSystemSettings?system=Testing&partnerId=${integration.Integration.CoId}`;
                await axios.put(url, { TestCaseGroup: e.target.value });
            } catch (err) {
                console.error('Failed to update system settings:', err);
                // Don't show error for this secondary call, the main update succeeded
            }
        }
    };
    const debouncedUpdateVendorNumber = useMemo(
        () => debounce(async (value: string, integrationId: string | number | undefined) => {
            if (!integrationId) {
                return;
            }
            try {
                await axios.post(Config.oauth + '/integration/OperatorUpdateVendorNumber/' + integrationId, { VendorNumber: value });
            } catch (err: any) {
                console.error('Failed to update vendor number:', err);
                let errorMsg = 'Unexpected error.';
                if (err.response && err.response.data && err.response.data.error) {
                    errorMsg = err.response.data.error;
                }
                setState({ showError: true, error: errorMsg });
            }
        }, 500),
        []
    );
    const setVendorNumber = (e: any) => {
        const value = e.target.value;
        setState({
            integration: {
                ...integration,
                Integration: {
                    ...integration.Integration,
                    VendorNumber: value
                }
            }
        });
        debouncedUpdateVendorNumber(value, integration?.Integration?.Id);
    };
    const reject = async (payload?: any) => {
        if (payload) {
            if (!integration) {
                return;
            }
            setState({ isSubmitting: true });
            try {
                await axios.post(Config.oauth + '/integration/operatorreject/' + integration.Integration.Id, payload);
                setState({ showReject: false, isSubmitting: false });
                await loadIntegration();
            } catch (err: any) {
                console.error('Failed to reject integration:', err);
                let errorMsg = 'Unexpected error.';
                if (err.response && err.response.data && err.response.data.error) {
                    errorMsg = err.response.data.error;
                }
                setState({ showError: true, error: errorMsg, isSubmitting: false });
            }
        } else {
            setState({ showReject: true, rejectMessage: '' });
        }
    };
    const rejectGoLive = async (payload?: any) => {
        if (payload) {
            if (!integration) {
                return;
            }
            setState({ isSubmitting: true });
            try {
                await axios.post(Config.oauth + '/integration/operatorrejectgolive/' + integration.Integration.Id, payload);
                setState({ showRejectGoLive: false, isSubmitting: false });
                await loadIntegration();
            } catch (err: any) {
                console.error('Failed to reject go live request:', err);
                let errorMsg = 'Unexpected error.';
                if (err.response && err.response.data && err.response.data.error) {
                    errorMsg = err.response.data.error;
                }
                setState({ showError: true, error: errorMsg, isSubmitting: false });
            }
        } else {
            setState({ showRejectGoLive: true, rejectMessage: '' });
        }
    };
    const openConfirm = (message: string, action: () => void) => {
        setState({ showConfirm: true, confirmMessage: message, onConfirm: action });
    };
    const accept = async (confirm?: boolean) => {
        if (confirm) {
            if (!integration) {
                return;
            }
            setState({ isSubmitting: true });
            try {
                await axios.post(Config.oauth + '/integration/operatoraccept/' + integration.Integration.Id);
                setState({ showConfirm: false, isSubmitting: false });
                await loadIntegration();
            } catch (e: any) {
                console.error('Failed to accept integration:', e);
                let errorMsg = 'Unexpected error.';
                if (e.response && e.response.data && e.response.data.error) {
                    errorMsg = e.response.data.error;
                }
                setState({ showConfirm: false, showError: true, error: errorMsg, isSubmitting: false });
            }
        } else {
            openConfirm('Are you sure you want to accept this integration request?', () => accept(true));
        }
    };
    const retry = async (confirm?: boolean) => {
        if (confirm) {
            if (!integration) {
                return;
            }
            setState({ isSubmitting: true });
            try {
                await axios.post(Config.oauth + '/integration/retry/' + integration.Integration.Id);
                setState({ showConfirm: false, isSubmitting: false });
                await loadIntegration();
            } catch (e: any) {
                console.error('Failed to retry integration:', e);
                let errorMsg = 'Unexpected error.';
                if (e.response && e.response.data && e.response.data.error) {
                    errorMsg = e.response.data.error;
                }
                setState({ showConfirm: false, showError: true, error: errorMsg, isSubmitting: false });
            }
        } else {
            openConfirm('Are you sure you want to retry the last action?', () => retry(true));
        }
    };
    const resendInvite = async (confirm?: boolean) => {
        if (confirm) {
            if (!integration) {
                return;
            }
            setState({ isSubmitting: true });
            try {
                await axios.post(Config.oauth + '/integration/resendinvite/' + integration.Integration.Id);
                setState({ showConfirm: false, isSubmitting: false });
                await loadIntegration();
            } catch (e: any) {
                console.error('Failed to resend invite:', e);
                let errorMsg = 'Unexpected error.';
                if (e.response && e.response.data && e.response.data.error) {
                    errorMsg = e.response.data.error;
                }
                setState({ showConfirm: false, showError: true, error: errorMsg, isSubmitting: false });
            }
        } else {
            openConfirm('Are you sure you want to resend a portal invitation to the primary contact?', () => resendInvite(true));
        }
    };
    const resendFirstEmail = async (email?: string) => {
        if (email) {
            if (!integration) {
                return;
            }
            setState({ isSubmitting: true });
            try {
                await axios.post(Config.oauth + '/integration/ResendInitialEmail/' + integration.Integration.Id, { Email: email });
                setState({ showResendFirstEmail: false, isSubmitting: false });
                await loadIntegration();
            } catch (e: any) {
                console.error('Failed to resend initial email:', e);
                let errorMsg = 'Unexpected error.';
                if (e.response && e.response.data && e.response.data.error) {
                    errorMsg = e.response.data.error;
                }
                setState({ showResendFirstEmail: false, showError: true, error: errorMsg, isSubmitting: false });
            }
        } else {
            setState({ showResendFirstEmail: true });
        }
    };

    const linkToExisting = async (coId?: string | number | null) => {
        if (coId) {
            if (!integration) {
                return;
            }
            setState({ isSubmitting: true });
            try {
                await axios.post(Config.oauth + '/integration/OperatorLinkToExisting/' + integration.Integration.Id, { CoId: coId });
                setState({ showLinkToExisting: false, isSubmitting: false });
                await loadIntegration();
            } catch (e: any) {
                console.error('Failed to link to existing account:', e);
                let errorMsg = 'Unexpected error.';
                if (e.response && e.response.data && e.response.data.error) {
                    errorMsg = e.response.data.error;
                }
                setState({ showLinkToExisting: false, showError: true, error: errorMsg, isSubmitting: false });
            }
        } else {
            // Load partners when opening modal
            try {
                // Fetch partners and non-ignored integrations so we can exclude partners already tied to an active one
                const [partners, integrations] = await Promise.all([
                    utilGetPartners(),
                    fetchAllODataPages<any>(Config.oauth + "/api/odata/integrations?$filter=CoId ne null and Priority ne 'Ignored'")
                ]);

                if (!partners.errorMessage) {
                    // Capture CoIds that already have an integration
                    const existingCoIds = new Set(
                        integrations
                            .map((i: any) => i.CoId)
                            .filter((coId: any) => coId !== null && coId !== undefined)
                            .map((coId: any) => coId.toString())
                    );

                    const formattedPartners: PartnerOption[] = partners
                        .map((p: any) => {
                            const coId = getPartnerCoId(p);
                            return { partner: p, coId: coId !== null ? coId.toString() : null };
                        })
                        .filter(({ coId }: any) => !!coId)                    // Ensure partner has a CoId to link
                        .filter(({ coId }: any) => !existingCoIds.has(coId))   // Exclude CoIds already tied to an integration
                        .sort((a: any, b: any) => a.partner.CompanyName.localeCompare(b.partner.CompanyName))
                        .map(({ partner, coId }: any) => ({ label: `${partner.CompanyName} (${coId})`, value: coId }));
                    setState({ showLinkToExisting: true, existingPartners: formattedPartners });
                } else {
                    setState({ showError: true, error: 'Failed to load partners.' });
                }
            } catch (e) {
                console.error('Failed to load partners:', e);
                setState({ showError: true, error: 'Failed to load partners.' });
            }
        }
    };

    const sendOnboardingReminder = async (confirm?: boolean) => {
        if (confirm) {
            if (!integration) {
                return;
            }
            setState({ isSubmitting: true });
            try {
                await axios.post(Config.oauth + '/integration/SendOnboardingReminder/' + integration.Integration.Id);
                setState({ showConfirm: false, isSubmitting: false });
                await loadIntegration();
            } catch (e: any) {
                console.error('Failed to send onboarding reminder:', e);
                let errorMsg = 'Unexpected error.';
                if (e.response && e.response.data && e.response.data.error) {
                    errorMsg = e.response.data.error;
                }
                setState({ showConfirm: false, showError: true, error: errorMsg, isSubmitting: false });
            }
        } else {
            openConfirm('Are you sure you want to send an onboarding reminder to the primary contact?', () => sendOnboardingReminder(true));
        }
    };
    const goLive = async (confirm?: boolean) => {
        if (confirm) {
            if (!integration) {
                return;
            }
            setState({ isSubmitting: true });
            try {
                await axios.post(Config.oauth + '/integration/golive/' + integration.Integration.Id);
                setState({ showConfirm: false, isSubmitting: false });
                await loadIntegration();
            } catch (e: any) {
                console.error('Failed to set integration to go live:', e);
                let errorMsg = 'Unexpected error.';
                if (e.response && e.response.data && e.response.data.error) {
                    errorMsg = e.response.data.error;
                }
                setState({ showConfirm: false, showError: true, error: errorMsg, isSubmitting: false });
            }
        } else {
            openConfirm('Are you sure you want to move this integration into production?', () => goLive(true));
        }
    };
    const editCustomFields = () => {
        if (!integration) {
            return;
        }
        if (isEditingCustomFields) {
            setState({ isEditingCustomFields: false, customFieldValues: {} });
            return;
        }
        const values: Record<string, string | null> = {};
        customFields.forEach(field => {
            values[field.Id] = (integration.Integration[field.Id] as string | null | undefined) ?? null;
        });
        setState({ isEditingCustomFields: true, customFieldValues: values });
    };
    const setCustomFieldValue = (id: string, value: string | null) => {
        setState({ customFieldValues: { ...customFieldValues, [id]: value } });
    };
    const customFieldsChanged = !!integration && isEditingCustomFields && customFields.some(field =>
        normalizeCustomFieldValue(customFieldValues[field.Id]) !== normalizeCustomFieldValue(integration.Integration[field.Id])
    );
    const saveCustomFields = async () => {
        if (!integration) {
            return;
        }
        // The endpoint compares every configured field against the stored
        // value (omitted fields would be cleared), so send all of them;
        // unchanged fields keep their original raw value to avoid logging a
        // null -> '' change.
        const data: Record<string, unknown> = {};
        customFields.forEach(field => {
            const original = integration.Integration[field.Id];
            const value = customFieldValues[field.Id];
            data[field.Id] = normalizeCustomFieldValue(value) === normalizeCustomFieldValue(original) ? (original ?? null) : (value || null);
        });
        setState({ isSubmitting: true });
        try {
            await axios.post(Config.oauth + '/integration/OperatorUpdateCustomFields/' + integration.Integration.Id, data);
            await loadIntegration();
            setState({ isEditingCustomFields: false, customFieldValues: {}, isSubmitting: false });
        } catch (e: any) {
            console.error('Failed to update custom fields:', e);
            let errorMsg = 'Unexpected error.';
            if (e.response && e.response.data && e.response.data.error) {
                errorMsg = e.response.data.error;
            }
            setState({ showError: true, error: errorMsg, isSubmitting: false });
        }
    };
    const toggleOnboardingForm = () => {
        setState({ showOnboardingForm: !showOnboardingForm });
    };
    const renderActionButton = useCallback((key: string, label: string, handler: () => void, variant: string = 'default') => {
        if (variant === 'primary') {
            return <Button variant="contained" color="primary" key={key} onClick={handler} disabled={isSubmitting}>{label}</Button>;
        }
        if (variant === 'danger') {
            return <Button variant="contained" color="error" key={key} onClick={handler} disabled={isSubmitting}>{label}</Button>;
        }
        return <Button variant="outlined" key={key} onClick={handler} disabled={isSubmitting}>{label}</Button>;
    }, [isSubmitting]);
    if (integration) {
        let model = integration;
        const actionButtons: React.ReactElement[] = [];
        if (model.CanOperatorAccept) {
            actionButtons.push(renderActionButton('accept', 'Accept', () => accept(), 'primary'));
            actionButtons.push(renderActionButton('reject', 'Reject', () => reject(), 'danger'));
        }
        if (model.CanRejectGoLive) {
            actionButtons.push(renderActionButton('rejectGoLive', 'Reject Request to go Live', () => rejectGoLive(), 'danger'));
        }
        if (model.Integration.Error) {
            actionButtons.push(renderActionButton('retry', 'Retry', () => retry()));
        }
        if (!model.Integration.Error && model.CanResendFirstEmail) {
            actionButtons.push(renderActionButton('resendFirstEmail', 'Resend Onboarding Invite', () => resendFirstEmail()));
        }
        if (!model.Integration.Error && (model.Integration.Status === 'Invite Sent' || model.Integration.Status === 'Form Received')) {
            actionButtons.push(renderActionButton('linkToExisting', 'Connect to Existing Account', () => linkToExisting()));
        }
        if (!model.Integration.Error && model.CanSendOnboardingReminder) {
            actionButtons.push(renderActionButton('sendOnboardingReminder', 'Send Onboarding Reminder', () => sendOnboardingReminder()));
        }
        if (!model.Integration.Error && model.CanResendInvite) {
            actionButtons.push(renderActionButton('resendInvite', 'Resend User Invite', () => resendInvite()));
        }
        if (!model.Integration.Error && model.CanGoLive) {
            actionButtons.push(renderActionButton('goLive', 'Push Supplier Live', () => goLive(), 'primary'));
        }
        const statusPillStyle = (color: string = model.StatusColor || COLORS.STATUS.NEW): React.CSSProperties => ({
            backgroundColor: color,
            color: COLORS.TEXT.WHITE,
            fontWeight: 600,
            height: 28,
            borderRadius: 999,
            padding: '0 12px',
            fontSize: '0.85rem'
        });
        const companyAddress = model.CompanyAddress || {};
        const renderFormRow = (label: React.ReactNode, value: React.ReactNode, key?: string) => (
            <div key={key || (label as string)} style={{ display: 'flex', justifyContent: 'space-between', gap: pageContentGap, marginBottom: '8px', alignItems: 'flex-start' }}>
                <Typography variant='caption' style={{ color: COLORS.TEXT.SECONDARY, fontWeight: 600, flex: '0 0 45%', maxWidth: '45%', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                    {label}
                </Typography>
                <Typography variant='body2' style={{ color: COLORS.TEXT.PRIMARY, flex: 1, textAlign: 'left', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                    {value || '-'}
                </Typography>
            </div>
        );
        const renderCustomFieldInput = (field: OnboardingCustomFieldConfig) => {
            const value = customFieldValues[field.Id] ?? null;
            const inputProps = { 'aria-label': field.Name };
            if (field.Type === 'select') {
                return (
                    <Autocomplete
                        size='small'
                        fullWidth
                        options={field.Options || []}
                        value={value || null}
                        onChange={(_e, newValue) => setCustomFieldValue(field.Id, newValue)}
                        disabled={isSubmitting}
                        renderInput={params => <TextField {...params} inputProps={{ ...params.inputProps, ...inputProps }} />}
                    />
                );
            }
            if (field.Type === 'multiselect') {
                return (
                    <Autocomplete
                        multiple
                        size='small'
                        fullWidth
                        options={field.Options || []}
                        value={splitMultiValue(value)}
                        onChange={(_e, newValue) => setCustomFieldValue(field.Id, newValue.join(','))}
                        disabled={isSubmitting}
                        renderInput={params => <TextField {...params} inputProps={{ ...params.inputProps, ...inputProps }} />}
                    />
                );
            }
            return (
                <TextField
                    size='small'
                    fullWidth
                    value={value || ''}
                    onChange={e => setCustomFieldValue(field.Id, e.target.value)}
                    disabled={isSubmitting}
                    slotProps={{ htmlInput: inputProps }}
                />
            );
        };
        const renderCustomFieldEditRow = (field: OnboardingCustomFieldConfig) => (
            <div key={field.Id} style={{ display: 'flex', justifyContent: 'space-between', gap: pageContentGap, marginBottom: '8px', alignItems: 'center' }}>
                <Typography variant='caption' style={{ color: COLORS.TEXT.SECONDARY, fontWeight: 600, flex: '0 0 45%', maxWidth: '45%', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                    {field.Name}{field.Required ? ' *' : ''}
                </Typography>
                <div style={{ flex: 1, minWidth: 0 }}>{renderCustomFieldInput(field)}</div>
            </div>
        );
        const generalInfoFields = [
            { label: 'Company name', value: model.Integration.CompanyName },
            { label: 'Account type', value: model.AccountType },
            { label: 'Primary contact first name', value: model.Integration.FirstName },
            { label: 'Primary contact last name', value: model.Integration.LastName },
            { label: 'Primary contact title', value: model.Integration.Title },
            { label: 'Primary contact phone', value: model.Integration.Phone },
            { label: 'Primary contact email address', value: model.Integration.Email },
            { label: 'Address line 1', value: companyAddress.Address1 },
            { label: 'Address line 2', value: companyAddress.Address2 },
            { label: 'City or town', value: companyAddress.City },
            { label: 'State', value: companyAddress.State },
            { label: 'Postal code', value: companyAddress.Zip },
            { label: 'Country', value: companyAddress.Country }
        ];
        const merchandisingInfoFields = [
            { label: 'Merchandiser name', value: `${model.Integration.MerchFirstName || ''} ${model.Integration.MerchLastName || ''}`.trim() },
            { label: 'Merchandiser title', value: model.Integration.MerchTitle },
            { label: 'Merchandiser email', value: model.Integration.MerchEmail },
            { label: 'Website', value: model.Integration.Website },
            { label: 'Category', value: (model.Integration.Category || '').split('|').filter(Boolean).join(', ') },
            { label: 'Subcategory', value: model.Integration.Subcategory }
        ];
        const retailerCompanyName = model.Integration.RetailerCompanyName
            || model.Integration.RetailerName
            || model.Integration.RequesterCompanyName
            || model.Integration.RequesterName
            || 'This retailer';
        const agreementContent = model.Integration.SignerName
            ? `Signed by ${model.Integration.SignerName} on ${moment(model.Integration.SignDate).format('MM/DD/YYYY hh:mm A')}`
            : <>
                {retailerCompanyName} does not have an agreement for suppliers to sign. To upload one, please go to the{' '}
                <MuiLink component={RouterLink} to="/onboarding/settings">Onboarding Settings</MuiLink> page.
            </>;
        const onboardingFormContentId = 'onboarding-form-content';
        return (
            <>
                <Grid container spacing={2} style={{ padding: "24px 14px" }}>
                    <Grid size={12}>
                        <Grid container alignItems="flex-start" spacing={2} wrap="wrap">
                            <Grid size="grow" style={{ display: 'flex', alignItems: 'center', gap: pageContentGap, minWidth: 0 }}>
                                <IconButton
                                    sx={iconButtonSx}
                                    style={{ height: '36px', width: '36px', position: 'relative', zIndex: 1 }}
                                    aria-label='Go back to onboarding'
                                    onClick={handleBack}
                                    disabled={!history}
                                    size="large">
                                    <ArrowBack style={{ height: '18px', width: '18px' }} />
                                </IconButton>
                                <Typography variant='h6' style={{ fontWeight: 600, color: COLORS.TEXT.PRIMARY, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                    {model.Integration.CompanyName}
                                </Typography>
                                <Chip label={model.Integration.Status || 'Onboarding'} style={statusPillStyle()} />
                                {model.Integration.Error && <Chip label="Error" style={statusPillStyle(COLORS.STATUS.ERROR)} />}
                            </Grid>
                            <Grid size="auto" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                                <div className='noPrint' style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'flex-end' }}>{actionButtons}</div>
                                <Typography variant='caption' style={{ color: COLORS.TEXT.SECONDARY, textAlign: 'right' }}>Started: <Moment format='MM/DD/YYYY h:mm A'>{model.Integration.StartDate}</Moment></Typography>
                            </Grid>
                        </Grid>
                    </Grid>
                    {model.Integration.Error && model.Integration.ErrorMessage && (
                        <Grid size={12}>
                            <Card id="cueMessage" role='alert' aria-live='assertive' style={{ backgroundColor: COLORS.STATUS.ERROR, color: COLORS.TEXT.WHITE }}>
                                <CardContent style={{ display: 'flex', alignItems: 'center' }}>
                                    <ErrorIcon aria-hidden='true' style={{ marginRight: '1rem' }} />
                                    <Typography style={{
                                        textAlign: "left",
                                        overflowY: "auto",
                                        overflowX: "hidden",
                                        maxHeight: "100px",
                                        whiteSpace: "pre-line"
                                    }}>
                                        Error: {model.Integration.ErrorMessage}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}
                    <Grid size={6}>
                        <Card>
                            <SectionToolbar>Supplier Details</SectionToolbar>
                            <CardContent style={{ textAlign: 'left', display: 'flex', flexDirection: 'column', paddingTop: '0px' }}>
                                <Accordion style={{ boxShadow: 'none' }} expanded>
                                    <AccordionDetails style={{ flexDirection: 'column', textAlign: 'left' }}>
                                        <WrappedField label="Company name">
                                            <TextField style={{ flex: 1 }} disabled value={model.Integration.CompanyName || ''} />
                                        </WrappedField>
                                        <WrappedField label="Logicbroker account number">
                                            <TextField style={{ flex: 1 }} disabled value={model.Integration.CoId || ''} />
                                        </WrappedField>
                                        <WrappedField label={
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                Vendor Number
                                                <Tooltip title="You can only edit the Vendor Number when a supplier is in the 'Invite Sent' or 'Form Received' status. For all other statuses, update the Vendor Number in the Inventory Feed Settings instead. Note that changes made there will not appear on this page.">
                                                    <IconButton size='small' aria-label='Vendor number info' style={{ padding: 0, color: '#6B7280' }}>
                                                        <InfoOutlined fontSize='small' />
                                                    </IconButton>
                                                </Tooltip>
                                            </span>
                                        }>
                                            <TextField
                                                style={{ width: '12em' }}
                                                disabled={Config.production || !(model.Integration.Status === 'Invite Sent' || model.Integration.Status === 'Form Received')}
                                                value={model.Integration.VendorNumber || ''}
                                                onChange={setVendorNumber}
                                            />
                                        </WrappedField>
                                        <WrappedField label={
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                Connection type
                                                <Tooltip title="Changing connection type will not change settings within Logicbroker after the company has been deployed to stage.">
                                                    <IconButton size='small' aria-label='Connection type info' style={{ padding: 0, color: '#6B7280' }}>
                                                        <InfoOutlined fontSize='small' />
                                                    </IconButton>
                                                </Tooltip>
                                            </span>
                                        }>
                                            <Select style={{ width: '12em' }} name="IntegrationType" value={model.Integration.IntegrationType || ''} onChange={setIntegrationType}>
                                                {(model.AllowedTypes || []).map(r => <MenuItem value={r} key={r}>{r}</MenuItem>)}
                                            </Select>
                                        </WrappedField>
                                        {(testCaseGroups as TestCaseGroupsConfig).Enabled && <WrappedField label="Test group">
                                            <Select style={{ width: '12em', paddingBottom: '0.5em' }} name="TestGroup" value={model.Integration.TestGroup || ''} onChange={setTestGroup}>
                                                <MenuItem value="">None</MenuItem>
                                                {(testCaseGroups as TestCaseGroupsConfig).Groups!.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                                            </Select>
                                        </WrappedField>}
                                        <WrappedField label="Priority">
                                            <Select style={{ width: '12em', paddingBottom: '0.5em' }} name="Priority" value={model.Integration.Priority || ''} onChange={setPriority}>
                                                <MenuItem value="">None</MenuItem>
                                                <MenuItem value="Ignored"><PriorityChip size="small" value="Ignored" /></MenuItem>
                                                <MenuItem value="Low"><PriorityChip size="small" value="Low" /></MenuItem>
                                                <MenuItem value="Medium"><PriorityChip size="small" value="Medium" /></MenuItem>
                                                <MenuItem value="High"><PriorityChip size="small" value="High" /></MenuItem>
                                            </Select>
                                        </WrappedField>
                                        <WrappedField label="Requested go live date">
                                            {!model.IsLive && (
                                                <TextField style={{ width: '12em' }} type="date" disabled={model.IsLive} value={model.Integration.RequestedLiveDate || ''} onChange={setGoLiveDate} slotProps={{ inputLabel: { shrink: true } }} />
                                            )}
                                        </WrappedField>
                                        <WrappedField label="Tags">
                                            <ExtendedSelect style={{ flex: 1 }} placeholder='Select tags...' value={model.Integration.Tags || []} options={tags} onChange={setTags} />
                                        </WrappedField>
                                        <WrappedField label="Disable reminders">
                                            <Switch style={{ flex: 1 }} checked={!(!model.Integration.DisableReminders)} onChange={setReminders} />
                                        </WrappedField>
                                    </AccordionDetails>
                                </Accordion>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={6} style={{ display: 'flex' }}>
                        <RetailerOnboarding partner={(!Config.production || (Config.production && model.IsLive)) ? model.Integration.CoId : null} partnerName={model.Integration.CompanyName} tasks={tasks} shippingLabelEnabled={shippingLabelEnabled} shippingLabelStatus={model.Integration.ShippingLabelStatus} integrationId={model.Integration.Id} canApproveShippingLabel={model.CanAcceptShippingLabel} canRejectShippingLabel={model.CanRejectShippingLabel} onShippingLabelChange={loadIntegration} useNewStyles />
                    </Grid>
                    <Grid size={12}>
                        <Card>
                            <SectionToolbar>
                                <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                    <Typography style={{ fontWeight: 600, fontSize: '20px' }}>Onboarding Form</Typography>
                                    <div style={{ flex: 1 }} />
                                    <IconButton
                                        size='small'
                                        onClick={toggleOnboardingForm}
                                        aria-label='Toggle onboarding form section'
                                        aria-expanded={showOnboardingForm}
                                        aria-controls={onboardingFormContentId}
                                    >
                                        {showOnboardingForm ? <ExpandLess /> : <ExpandMore />}
                                    </IconButton>
                                </div>
                            </SectionToolbar>
                            {showOnboardingForm && <CardContent id={onboardingFormContentId}>
                                <Grid container spacing={4} style={{ textAlign: 'left' }}>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <Typography style={{ fontSize: '16px', marginBottom: '12px', fontWeight: "bold" }}>General info</Typography>
                                        {generalInfoFields.map(field => renderFormRow(field.label, field.value))}
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <Typography style={{ fontSize: '16px', marginBottom: '12px', fontWeight: "bold" }}>Merchandising info</Typography>
                                        {merchandisingInfoFields.map(field => renderFormRow(field.label, field.value))}
                                        <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: `1px solid ${COLORS.UI.BORDER}` }}>
                                            <Typography style={{ fontSize: '16px', marginBottom: '8px', fontWeight: "bold" }}>Supplier agreement</Typography>
                                            <Typography variant='body2' style={{ color: COLORS.TEXT.PRIMARY, lineHeight: 1.5 }}>
                                                {agreementContent}
                                            </Typography>
                                        </div>
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <Typography style={{ fontSize: '16px', marginBottom: '12px', fontWeight: "bold" }}>Custom questions</Typography>
                                        {(customFields || []).length
                                            ? <>
                                                {customFields.map(field => isEditingCustomFields
                                                    ? renderCustomFieldEditRow(field)
                                                    : renderFormRow(field.Name, model.Integration[field.Id] as React.ReactNode, field.Id))}
                                                <div className='noPrint' style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                                                    <Button variant='outlined' onClick={editCustomFields} disabled={isSubmitting}>{isEditingCustomFields ? 'Cancel' : 'Edit'}</Button>
                                                    <Button variant='outlined' color='primary' onClick={saveCustomFields} disabled={!customFieldsChanged || isSubmitting}>Save</Button>
                                                </div>
                                            </>
                                            : <Typography variant='body2' style={{ color: COLORS.TEXT.PRIMARY, lineHeight: 1.5 }}>
                                                {retailerCompanyName} does not have any custom questions for suppliers to fill out. To add custom questions, please go to the{' '}
                                                <MuiLink component={RouterLink} to="/onboarding/settings">Onboarding Settings</MuiLink> page.
                                            </Typography>}
                                    </Grid>
                                </Grid>
                            </CardContent>}
                        </Card>
                    </Grid>
                    {model.Logs && model.Logs.length ?
                        <Grid size={12}>
                            <Typography variant='h6' style={{ textAlign: 'left' }}>Events</Typography>
                            <Timeline style={{ fontSize: '100%', margin: '0px 0px 0px 10px' }}>
                                {model.Logs.map(log =>
                                    <Log key={log.Id} log={log} />
                                )}
                            </Timeline>
                        </Grid>
                        : ''}
                </Grid>
                <Dialog open={showReject} onClose={() => { setState({ showReject: false }) }} fullWidth maxWidth="xs">
                    <Toolbar className='lbtoolbar'>Reject Integration</Toolbar>
                    <Form onSubmit={reject}
                        render={({ handleSubmit, pristine, invalid, values, form }) => (
                            <form autoComplete='off' onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                <DialogContent>
                                    <div style={{ display: 'flex', flex: '1 1 auto', position: 'relative' }}>
                                        <Field style={{ width: '100%' }} validate={v => v ? undefined : 'Reason is required.'} label='Rejection reason' component={ExtendedField} name='RejectionReason' />
                                    </div>
                                </DialogContent>
                                <DialogActions>
                                    <DownloadButton color="primary" onClick={handleSubmit} type='submit' disabled={pristine || invalid || isSubmitting}>Reject</DownloadButton>
                                </DialogActions>
                            </form>
                        )} />
                </Dialog>
                <Dialog open={showRejectGoLive} onClose={() => { setState({ showRejectGoLive: false }) }} fullWidth maxWidth="xs">
                    <Toolbar className='lbtoolbar'>Reject Request To Go Live</Toolbar>
                    <Form onSubmit={rejectGoLive}
                        render={({ handleSubmit, pristine, invalid, values, form }) => (
                            <form autoComplete='off' onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                <DialogContent>
                                    <div style={{ display: 'flex', flex: '1 1 auto', position: 'relative' }}>
                                        <Field style={{ width: '100%' }} validate={v => v ? undefined : 'Reason is required.'} label='Rejection reason' component={ExtendedField} name='RejectionReason' />
                                    </div>
                                </DialogContent>
                                <DialogActions>
                                    <DownloadButton color="primary" onClick={handleSubmit} type='submit' disabled={pristine || invalid || isSubmitting}>Reject</DownloadButton>
                                </DialogActions>
                            </form>
                        )} />
                </Dialog>
                <Dialog open={showResendFirstEmail} onClose={() => { setState({ showResendFirstEmail: false }) }} fullWidth maxWidth="xs">
                    <Toolbar className='lbtoolbar'>Resend Onboarding Invite</Toolbar>
                    <Form onSubmit={(values: any) => resendFirstEmail(values.Email)}
                        initialValues={{ Email: integration?.Integration?.Email || '' }}
                        render={({ handleSubmit, pristine, invalid, values, form }) => (
                            <form autoComplete='off' onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                <DialogContent>
                                    <div style={{ display: 'flex', flex: '1 1 auto', position: 'relative' }}>
                                        <Field
                                            style={{ width: '100%' }}
                                            validate={v => v ? validateEmail(v) : 'Email address is required.'}
                                            label='Primary contact email address'
                                            type='email'
                                            component={ExtendedField}
                                            name='Email'
                                        />
                                    </div>
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={() => setState({ showResendFirstEmail: false })} variant="text" disabled={isSubmitting}>Cancel</Button>
                                    <DownloadButton color="primary" onClick={handleSubmit} type='submit' disabled={invalid || isSubmitting}>Send</DownloadButton>
                                </DialogActions>
                            </form>
                        )} />
                </Dialog>
                <Dialog open={showLinkToExisting} onClose={() => { setState({ showLinkToExisting: false }) }} fullWidth maxWidth="sm">
                    <Toolbar className='lbtoolbar'>Connect to Existing Account</Toolbar>
                    <Form onSubmit={(values: any) => linkToExisting(values.CoId)}
                        render={({ handleSubmit, invalid }) => (
                            <form autoComplete='off' onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                <DialogContent>
                                    <Typography style={{ marginBottom: '16px' }}>
                                        If this supplier already has an active Logicbroker account, you can link them here. Select the existing supplier from the dropdown to connect them to this onboarding. Their status will update, and they can continue the onboarding process without starting over.
                                    </Typography>
                                    <Field
                                        name='CoId'
                                        validate={v => v ? undefined : 'Please select a supplier.'}
                                        render={({ input }) => (
                                            <ExtendedSelect
                                                style={{ width: '100%' }}
                                                placeholder='Select existing supplier...'
                                                options={existingPartners}
                                                value={input.value}
                                                onChange={(val: any) => input.onChange(val)}
                                            />
                                        )}
                                    />
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={() => setState({ showLinkToExisting: false })} variant="text" disabled={isSubmitting}>Cancel</Button>
                                    <DownloadButton color="primary" onClick={handleSubmit} type='submit' disabled={invalid || isSubmitting}>Connect</DownloadButton>
                                </DialogActions>
                            </form>
                        )} />
                </Dialog>
                <Dialog open={showError} onClose={() => { setState({ showError: false }) }}>
                    <Toolbar className='lbtoolbar'>{'Error'}</Toolbar>
                    <DialogContent><Typography>{error}</Typography></DialogContent>
                </Dialog>
                <ConfirmDialog open={showConfirm} onClose={() => setState({ showConfirm: false })} message={confirmMessage} onConfirm={onConfirm} />
            </>
        );
    }
    return (
        <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                <ContentLoader preserveAspectRatio='none' style={{ height: '6.5em', width: '100%' }} primaryColor={COLORS.UI.LOADER_PRIMARY} secondaryColor={COLORS.UI.LOADER_SECONDARY} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                <ContentLoader preserveAspectRatio='none' style={{ height: '50em', width: '100%' }} primaryColor={COLORS.UI.LOADER_PRIMARY} secondaryColor={COLORS.UI.LOADER_SECONDARY} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                <ContentLoader preserveAspectRatio='none' style={{ height: '50em', width: '100%' }} primaryColor={COLORS.UI.LOADER_PRIMARY} secondaryColor={COLORS.UI.LOADER_SECONDARY} />
            </Grid>
        </Grid>
    );
};
export default OnboardingDetailsV2;
